"use client";

import { useState } from "react";
import { useLocale } from "next-intl";
import { type Locale } from "@/i18n/config";

const BREVO_FORMS: Record<Locale, string> = {
  es: "https://7bbd51fc.sibforms.com/serve/MUIFAIyTXpI_-qqw36jmDU9C2ycVJZkYEQ3Q0nBZdFx6aBhW970niMJAhcgnnPkRze2PmtlBX7H2JPoskli_Xw6EYqjVGBZ0GuNt12XiHvzb9KZq6K37oZP0FNJ3z-aDRZC-WZXRwGzL3b-rbE2GWz0tyHPLiHsgg4k-aC8qZhdh9JqgWSXWmfGpSj3ZLZcoMikg861lMWWan9BG",
  en: "https://7bbd51fc.sibforms.com/serve/MUIFAF9ruxI8XHw3cHleB2wxM5kurgT-8ngqXv2jecgRxhctQTZNgsbCwu-EC8i1g64JEFWx0p8twsslQTsk1k3Ro_eLyO6MtSkFsS2Kw0Pe5IR63u4IuOYBc3CmdqkniDKXA5Eiy8VFLNSFTWMkG6160lLMowJz4sJ4ljvHeTa6QZ96Le8A4zUtxHGe6GymGI4pu5-QJG9o6pat",
  it: "https://7bbd51fc.sibforms.com/serve/MUIFAPyvP1v5zt3v2f6LYN2fDjYl52yYPvQO_ioNx8Zdm-xrKSF0vxOt2M9908Qq8ohHG1A3ZUwn_fA5zdvVoXY8iXZwGFiTHRy3Nq5xKZ3kHSjZzLZxcmtH0xTXEKyLLg5za2xvyFjIQAsC5P9N3GPvH_P5-CvGKe-GhfuTvzxXarJfIoO_knWJCiuUDajRmJ34ZrMJif5sNOFr",
};

const FORM_CONTENT: Record<
  Locale,
  {
    title: string;
    description: string;
    emailPlaceholder: string;
    submitButton: string;
    successTitle: string;
    successMessage: string;
    submitting: string;
  }
> = {
  es: {
    title: "Prueba nuestra clase gratuita",
    description:
      "Introduce tu email y te enviaremos acceso a una clase completa de salsa.",
    emailPlaceholder: "Introduce tu email",
    submitButton: "Enviar",
    successTitle: "¡Clase Enviada!",
    successMessage:
      "Hemos enviado el enlace a tu clase gratuita. Revisa tu bandeja de entrada (y la carpeta de spam).",
    submitting: "Enviando...",
  },
  en: {
    title: "Try our free class",
    description:
      "Enter your email and we'll send you access to a complete salsa class.",
    emailPlaceholder: "Enter Email Address",
    submitButton: "Submit",
    successTitle: "Class Sent!",
    successMessage:
      "We've sent the link to your free class. Check your inbox (and spam folder).",
    submitting: "Sending...",
  },
  it: {
    title: "Prova la nostra lezione gratuita",
    description:
      "Inserisci la tua email e ti invieremo l'accesso a una lezione completa di salsa.",
    emailPlaceholder: "Inserisci la tua email",
    submitButton: "Invia",
    successTitle: "Lezione Inviata!",
    successMessage:
      "Abbiamo inviato il link alla tua lezione gratuita. Controlla la tua casella di posta (e la cartella spam).",
    submitting: "Invio...",
  },
};

export function SubscriptionForm() {
  const locale = useLocale() as Locale;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");

  const formUrl = BREVO_FORMS[locale];
  const content = FORM_CONTENT[locale];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);

    try {
      await fetch(formUrl, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      }, 1000);
    } catch {
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      }, 1000);
    }
  };

  if (isSubmitted) {
    return (
      <section className="w-full max-w-2xl mx-auto">
        <div className="text-center space-y-4 p-8 bg-zinc-900/50 border border-white/10">
          <h3 className="text-2xl font-bold text-white">
            {content.successTitle}
          </h3>
          <p className="text-gray-300">{content.successMessage}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-white">
        {content.title}
      </h2>
      <p className="text-gray-300 mb-6 text-lg">{content.description}</p>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          name="EMAIL"
          placeholder={content.emailPlaceholder}
          required
          disabled={isSubmitting}
          className="flex-1 px-4 py-3 bg-zinc-900 border border-white/20 text-white placeholder:text-gray-500 focus:outline-none focus:border-white/40 transition-colors disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="border-2 border-white/30 hover:border-white/50 hover:bg-white/10 text-white px-8 py-3 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? content.submitting : content.submitButton}
        </button>

        {/* Hidden Brevo fields */}
        <input
          type="text"
          name="email_address_check"
          defaultValue=""
          className="hidden"
        />
        <input type="hidden" name="locale" defaultValue={locale} />
        <input type="hidden" name="html_type" defaultValue="simple" />
      </form>
    </section>
  );
}
